import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";
import Tippy from "@tippyjs/react";
import { ToggleOff, ToggleOn } from "@mui/icons-material";
import moment from "moment";

import { ruleDevicesByRuleId, rulesByCompanyID } from "graphql/queries";
import { deleteRule, deleteRuleDevice, updateRule } from "graphql/mutations";
import { customGetRule, getCompanyName } from "graphql/custom-queries";
import usePermissions from "hooks/usePermission";
import { RuleCreateForm, RuleUpdateForm } from ".";
import { LayoutContext } from "layout/context/LayoutContext";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";

const Rules = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "rule:create-config",
    "rule:update-config",
    "rule:delete-config",
  ]);
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ruleDetails, setRuleDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    ruleId: null,
    ruleName: null,
  });
  const [modalRule, setModalRule] = useState({
    visible: false,
    ruleId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      rules.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        actions: (
          <>
            <Button
              className="px-1"
              children={
                ruleDetails?.id === item.id
                  ? strings.actions.hide
                  : strings.actions.show
              }
              variation="link"
              size="small"
              onClick={() => {
                ruleDetails?.id === item.id
                  ? setRuleDetails(null)
                  : fetchRule(item.id);
              }}
            />
            {canUpdate && (
              <Button
                className="px-1"
                children={strings.actions.edit}
                variation="link"
                size="small"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <Button
                className="px-1"
                children={strings.actions.delete}
                variation="link"
                size="small"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    ruleId: item.id,
                    ruleName: item.name,
                  });
                }}
              />
            )}
            {canUpdate && (
              <>
                {!!item.enabled ? (
                  <Tippy content={strings.actions.deactivate}>
                    <ToggleOn
                      onClick={() => ruleToggle(item.id, !item.enabled)}
                      style={styles.iconAction}
                      color="success"
                    ></ToggleOn>
                  </Tippy>
                ) : (
                  <Tippy content={strings.actions.activate}>
                    <ToggleOff
                      onClick={() => ruleToggle(item.id, !item.enabled)}
                      style={styles.iconAction}
                      color="error"
                    ></ToggleOff>
                  </Tippy>
                )}
              </>
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rules, ruleDetails?.id]
  );

  const onNext = () => {
    fetchRules(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchRules(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchRules(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalRule({ visible: true, action, ruleId: id });
  const onSuccessAction = () => {
    setModalRule({ visible: false });
    fetchRules();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByRules = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setRules((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
        };

        return updatedList;
      });
    });
  };

  async function fetchRules(token, isNext = true, keywords) {
    const nameOperation = rulesByCompanyID;
    const nameOperationStr = "rulesByCompanyID";
    try {
      setRulesLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      const rulesData = await API.graphql(
        graphqlOperation(nameOperation, {
          companyID: searchBy.company.id || me.companyID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      const rulesItems = rulesData.data[nameOperationStr].items;
      setRules(rulesItems);
      getNamesByRules(rulesItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: rulesData.data[nameOperationStr].nextToken,
        };
      });

      setRulesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setRulesLoading(false);
    } finally {
      setRulesLoading(false);
    }
  }
  async function fetchRule(id) {
    try {
      const ruleData = await API.graphql(
        graphqlOperation(customGetRule, {
          id,
        })
      );
      const ruleItem = ruleData.data.getRule;
      setRuleDetails({
        ...ruleItem,
        devices: ruleItem.devices?.items?.map((item) => item.device.name),
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }
  async function ruleDelete(id) {
    try {
      await ruleDevicesDelete(id);

      const result = await API.graphql(
        graphqlOperation(deleteRule, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.deleteRule.id;
      setRules(rules.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        ruleId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  async function ruleDevicesDelete(ruleId) {
    try {
      const results = await API.graphql(
        graphqlOperation(ruleDevicesByRuleId, { ruleId })
      );
      const rulesDeviceToDelete = results.data.ruleDevicesByRuleId.items;
      await Promise.all(
        rulesDeviceToDelete?.map((ruleDevice) =>
          API.graphql(
            graphqlOperation(deleteRuleDevice, {
              input: { id: ruleDevice.id },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones", err);
    }
  }

  async function ruleToggle(id, value) {
    try {
      await API.graphql(
        graphqlOperation(updateRule, { input: { id, enabled: value } })
      );
      toast.success(strings.successUpdated);
      setRules(
        rules.map((item) =>
          item.id === id ? { ...item, enabled: value } : item
        )
      );
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "30%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "30%" },
    },
    {
      key: "description",
      label: strings.table.headers.description,
      _style: { width: "20%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.actions.addRule}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {rulesLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!ruleDetails?.id} style={styles.collapseCard}>
          <CCard key={ruleDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{ruleDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b> {ruleDetails?.description}
              </p>
              <div className="text-muted">
                <b>{strings.details.devices}:</b>
                <ul>
                  {ruleDetails?.devices?.map((device, index) => (
                    <li key={index}>{device}</li>
                  ))}
                </ul>
              </div>
              <div className="text-muted">
                <b>{strings.details.emailNotifications}:</b>
                <ul>
                  {ruleDetails?.emailNotifications?.map((it, index) => (
                    <li key={index}>{it}</li>
                  ))}
                </ul>
              </div>
              <div className="text-muted">
                <b>{strings.details.wspNotifications}:</b>
                <ul>
                  {ruleDetails?.wspNotifications?.map((it, index) => (
                    <li key={index}>{it}</li>
                  ))}
                </ul>
              </div>
              <div className="text-muted">
                <b>{strings.details.smsNotifications}:</b>
                <ul>
                  {ruleDetails?.smsNotifications?.map((it, index) => (
                    <li key={index}>{it}</li>
                  ))}
                </ul>
              </div>
              <p className="text-muted">
                <b>{strings.details.endSchedule}:</b> {ruleDetails?.endSchedule}
              </p>
              <p className="text-muted">
                <b>{strings.details.startSchedule}:</b>{" "}
                {ruleDetails?.startSchedule}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(ruleDetails?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <CModal
        alignment="center"
        visible={modalDetails.visible}
        onClose={() => setModalDetails({ visible: false, ruleId: null })}
        backdrop="static"
      >
        <CModalBody>
          <b>
            {strings.formatString(strings.modal.confirmDeleteQuestion, {
              name: modalDetails.ruleName,
            })}
          </b>
        </CModalBody>
        <CModalFooter>
          <Button
            size="small"
            onClick={() => setModalDetails({ visible: false, ruleId: null })}
          >
            {strings.modal.cancelButton}
          </Button>
          <Button
            size="small"
            variation="primary"
            onClick={() => ruleDelete(modalDetails.ruleId)}
          >
            {strings.modal.confirmButton}
          </Button>
        </CModalFooter>
      </CModal>
      {modalRule.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalRule.visible}
          onClose={() => setModalRule({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalRule.action === "create"
                ? strings.modalRule.addTitle
                : strings.modalRule.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalRule.action === "create" ? (
                <RuleCreateForm onSuccess={onSuccessAction} />
              ) : (
                <RuleUpdateForm
                  ruleId={modalRule.ruleId}
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Configuración de Reglas",
    successDeleted: "Regla eliminada con éxito",
    successUpdated: "Regla actualizada con éxito",
    actions: {
      addRule: "Agregar Regla",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      activate: "Activar",
      deactivate: "Desactivar",
    },
    responses: {
      errorFetch: "Error al consultar reglas",
    },
    table: {
      headers: {
        companyName: "Empresa",
        name: "Nombre",
        description: "Descripción",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles de regla",
      devices: "Dispositivos",
      emailNotifications: "Notificaciones por Correo",
      wspNotifications: "Notificaciones por Whatsapp",
      smsNotifications: "Notificaciones por SMS",
      description: "Descripción",
      endSchedule: "Hora de Fin",
      startSchedule: "Hora de Inicio",
      action: "Acción",
      actionValue: "Valor de acción",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar la regla {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalRule: {
      addTitle: "Agregar Regla",
      updateTitle: "Actualizar Regla",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
  iconAction: {
    cursor: "pointer",
    fontSize: "30px",
  },
};

export default Rules;
